const WIRE_CAPABILITIES = Object.freeze({
  operatorStateV1: 'operator_state_v1',
  indexProgressV1: 'index_progress_v1',
  onboardingCheckpointsV1: 'onboarding_checkpoints_v1',
  catalogSelectorsV1: 'catalog_selectors_v1',
  availabilityRulesV1: 'availability_rules_v1',
  payloadProvidersV1: 'payload_providers_v1',
  variantPresentationSettingsV1: 'variant_presentation_settings_v1',
  variantGroupingV1: 'variant_grouping_v1',
  diagnosticEventsV1: 'diagnostic_events_v1',
  diagnosticSnapshotV1: 'diagnostic_snapshot_v1',
  publishStateV1: 'publish_state_v1',
  liveEnabledV1: 'live_enabled_v1',
  telemetryPolicyV1: 'telemetry_policy_v1',
  sharedAdminRuntimeV1: 'shared_admin_runtime_v1',
  settingsV2: 'settings_v2',
  searchCustomizationV1: 'search_customization_v1',
  analyticsV1: 'analytics_v1',
  analyticsPopularProductsV1: 'analytics_popular_products_v1',
  analyticsQueryDetailV1: 'analytics_query_detail_v1',
  analyticsReportExportV1: 'analytics_report_export_v1',
  deferredReindexV1: 'deferred_reindex_v1',
  facetPolicyEditorV1: 'facet_policy_editor_v1',
  emptyQueryV1: 'empty_query_v1',
  facetValueMappingsV1: 'facet_value_mappings_v1',
  cardTemplateEditor: 'card_template_editor_v1',
  merchandising_bridge_v1: 'merchandising_bridge_v1',
  merchandising_rules_v1: 'merchandising_rules_v1',
  category_priority_v2: 'category_priority_v2',
  merchandising_preview_v1: 'merchandising_preview_v1',
  merchandising_explain_v1: 'merchandising_explain_v1',
  merchandising_query_groups_v1: 'merchandising_query_groups_v1',
  merchandising_csv_v1: 'merchandising_csv_v1',
  merchandising_search_terms_v1: 'merchandising_search_terms_v1',
  merchandising_advanced_conditions_v1: 'merchandising_advanced_conditions_v1',
  agentMcpAdminV1: 'agent_mcp_admin_v1',
  agentUiControlPlaneV1: 'agent_ui_control_plane_v1',
  catalogFeedAdminV1: 'catalog_feed_admin_v1',
  storefrontContentV1: 'storefront_content_v1',
  contextualPricingAdminV1: 'contextual_pricing_admin_v1',
  catalogUpdatePolicyV1: 'catalog_update_policy_v1',
  descriptionParametersV1: 'description_parameters_v1',
  descriptionParametersCatalogPreparationV1: 'description_parameters_catalog_preparation_v1',
  descriptionParametersLifecycleV1: 'description_parameters_lifecycle_v1',
  descriptionParametersNormalizationV1: 'description_parameters_normalization_v1',
  descriptionParametersNormalizationV2: 'description_parameters_normalization_v2',
  catalogUpdateScheduleV1: 'catalog_update_schedule_v1',
});

const LEGACY_SERVICE_CAPABILITIES = Object.freeze(['analytics', 'synonyms', 'facets', 'index']);

/**
 * Canonical admin projection of the plugin's wire-level capability map.
 *
 * Missing keys are deliberately false. A module version can preserve a proven
 * legacy contract elsewhere, but it must never unlock a new 1.0.10 workflow.
 */
export function normalizePluginCapabilities(rawCapabilities = {}, overrides = {}) {
  const raw = rawCapabilities && typeof rawCapabilities === 'object'
    ? rawCapabilities
    : {};
  const normalized = {};

  Object.entries(WIRE_CAPABILITIES).forEach(([adminKey, wireKey]) => {
    normalized[adminKey] = raw[wireKey] === true || raw[adminKey] === true;
  });
  // This capability was introduced after the original plugin handshake. Keep
  // the shape of old sessions stable while treating an omitted key as disabled.
  if (!Object.prototype.hasOwnProperty.call(raw, 'merchandising_advanced_conditions_v1')) {
    delete normalized.merchandising_advanced_conditions_v1;
  }
  LEGACY_SERVICE_CAPABILITIES.forEach((key) => {
    normalized[key] = false;
  });

  return { ...normalized, ...overrides };
}

/** Save-only source review requires native preparation and the shared rebuild flow. */
export function supportsDescriptionCatalogWorkflow(capabilities = {}) {
  return capabilities.descriptionParametersV1 === true
    && capabilities.descriptionParametersCatalogPreparationV1 === true
    && capabilities.descriptionParametersLifecycleV1 === true
    && capabilities.deferredReindexV1 === true;
}

/**
 * One product-neutral compatibility model consumed by operator workflows.
 * Modes describe behavior, while capability booleans remain transport facts.
 */
export function createAdminCompatibilityModel({
  productId = 'plugin',
  platform = null,
  capabilities = {},
} = {}) {
  const deferredCatalogUpdates = capabilities.deferredReindexV1 === true;
  const advancedFilterEditor = capabilities.facetPolicyEditorV1 === true;
  const queryAnalyticsDetail = capabilities.analyticsQueryDetailV1 === true;
  const managedCatalogFeedAccess = productId === 'plugin'
    && capabilities.catalogFeedAdminV1 === true;
  const managedContextualPricing = productId === 'plugin'
    && platform === 'prestashop'
    && capabilities.contextualPricingAdminV1 === true;
  const managedCatalogUpdatePolicy = productId === 'plugin'
    && platform === 'prestashop'
    && capabilities.catalogUpdatePolicyV1 === true;
  const managedCatalogUpdateSchedule = managedCatalogUpdatePolicy
    && capabilities.catalogUpdateScheduleV1 === true;
  const descriptionParametersAvailable = productId === 'plugin'
    && platform === 'prestashop'
    && supportsDescriptionCatalogWorkflow(capabilities);

  return Object.freeze({
    productId,
    catalogUpdates: Object.freeze({
      mode: deferredCatalogUpdates ? 'deferred' : 'immediate',
      deferred: deferredCatalogUpdates,
    }),
    filters: Object.freeze({
      mode: advancedFilterEditor ? 'advanced' : 'ordering-only',
      advancedEditor: advancedFilterEditor,
      valueMappings: advancedFilterEditor && capabilities.facetValueMappingsV1 === true,
    }),
    queryAnalytics: Object.freeze({
      mode: queryAnalyticsDetail ? 'detail' : 'summary-only',
      detail: queryAnalyticsDetail,
      export: capabilities.analyticsReportExportV1 === true,
    }),
    catalogFeedAccess: Object.freeze({
      mode: managedCatalogFeedAccess ? 'managed' : 'unavailable',
      managed: managedCatalogFeedAccess,
    }),
    contextualPricing: Object.freeze({
      mode: managedContextualPricing ? 'managed' : 'unavailable',
      managed: managedContextualPricing,
    }),
    catalogUpdatePolicy: Object.freeze({
      mode: managedCatalogUpdatePolicy ? 'managed' : 'unavailable',
      managed: managedCatalogUpdatePolicy,
      scheduleManaged: managedCatalogUpdateSchedule,
    }),
    descriptionParameters: Object.freeze({
      available: descriptionParametersAvailable,
      lifecycleAvailable: descriptionParametersAvailable
        && capabilities.descriptionParametersLifecycleV1 === true,
      normalizationAvailable: descriptionParametersAvailable
        && capabilities.descriptionParametersNormalizationV2 === true,
    }),
  });
}

export { WIRE_CAPABILITIES };
